import React from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/FacebookOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import MuiLoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';

const STYLES = { height: '28px', width: '28px' };

export function AddButton({ onClick, disabled }) {
  return (
    <IconButton
      aria-label="add"
      style={STYLES}
      onClick={onClick}
      disabled={disabled}
      size="large"
    >
      <AddIcon />
    </IconButton>
  );
}

AddButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function DeleteButton({ onClick, disabled }) {
  return (
    <IconButton
      aria-label="delete"
      style={STYLES}
      onClick={onClick}
      disabled={disabled}
      size="large"
    >
      <DeleteIcon />
    </IconButton>
  );
}

DeleteButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function ImportButton({ onClick, disabled, loading }) {
  return (
    <MuiLoadingButton
      disabled={disabled}
      onClick={onClick}
      endIcon={<CloudDownloadIcon />}
      loading={loading}
      loadingPosition="end"
      color="secondary"
      variant="contained"
    >
      Import
    </MuiLoadingButton>
  );
}

ImportButton.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function RefreshButton({ onClick, disabled }) {
  return (
    <IconButton
      aria-label="refresh"
      style={STYLES}
      onClick={onClick}
      disabled={disabled}
      size="large"
    >
      <RefreshIcon />
    </IconButton>
  );
}

RefreshButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function ButtonWrapper({ className, onAdd, onDelete, disabled }) {
  return (
    <div className={className}>
      <AddButton onClick={onAdd} disabled={disabled} />
      <DeleteButton onClick={onDelete} disabled={disabled} />
    </div>
  );
}

ButtonWrapper.propTypes = {
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export function LoadingButton({ loading, disabled, children, ...props }) {
  return (
    <Button variant="contained" {...props} disabled={disabled || loading}>
      {loading ? <CircularProgress size={24} /> : children}
    </Button>
  );
}

LoadingButton.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export function SetAllButton({ onClick, disabled }) {
  return (
    <Tooltip title="Set value to all creatives">
      <span>
        <IconButton
          aria-label="set-all"
          onClick={onClick}
          disabled={disabled}
          size="large"
        >
          <DoneAllIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
}

SetAllButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function ClearAllButton({ onClick, disabled, className, ...props }) {
  return (
    <Tooltip title="Clear project data">
      <span className={className}>
        <Button
          variant="outlined"
          aria-label="clear-all"
          onClick={onClick}
          disabled={disabled}
          startIcon={<ClearAllIcon />}
          {...props}
        >
          Clear
        </Button>
      </span>
    </Tooltip>
  );
}

ClearAllButton.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function PresetButton({ className, ...props }) {
  return (
    <span className={className}>
      <Button
        variant="outlined"
        aria-label="preset"
        startIcon={<AddCircleIcon />}
        {...props}
      >
        Preset
      </Button>
    </span>
  );
}

PresetButton.propTypes = {
  className: PropTypes.string,
};

export const ExpandButton = styled(props => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export function GoogleButton({ size, ...props }) {
  return (
    <IconButton aria-label="google" {...props}>
      <GoogleIcon fontSize={size} />
    </IconButton>
  );
}

GoogleButton.propTypes = {
  size: PropTypes.string,
};

export function FacebookButton({ size, ...props }) {
  return (
    <IconButton aria-label="facebook" {...props}>
      <FacebookIcon fontSize={size} />
    </IconButton>
  );
}

FacebookButton.propTypes = {
  size: PropTypes.string,
};

export function GenerateButton({ onClick, disabled }) {
  return (
    <Tooltip title="Generate">
      <span>
        <IconButton
          aria-label="generate"
          onClick={onClick}
          disabled={disabled}
          size="large"
        >
          <SmartToyIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
}

GenerateButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function CopyToClipboardButton({ onClick, disabled }) {
  return (
    <Tooltip title="Copy to clipboard">
      <span>
        <IconButton
          aria-label="copy to clipboard"
          onClick={onClick}
          disabled={disabled}
          size="large"
        >
          <ContentCopyIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
}

CopyToClipboardButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function LinkButton({ href, ...props }) {
  return (
    <IconButton
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      <OpenInNewIcon />
    </IconButton>
  );
}

LinkButton.propTypes = { href: PropTypes.string.isRequired };
